// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import TicketBarcodeInput from "./TicketBarcodeInput";
import TicketImageField from "./TicketImageField";
import { useToasts } from "react-toast-notifications";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import ticketService from "services/ticketService";
import TicketSaleAssignmentFields from "./TicketSaleAssignmentFields";
import {
  buildTicketAssigneesPayload,
  validateTicketAssigneeSelections,
} from "./ticketSaleAssignmentUtils";
import {
  extractTicketUploadResourceId,
  mapTicketTypeOptions,
  normalizeReceiveBusOrderRefCode,
} from "./utils";

const getInitialFormValue = () => ({
  ticketType: null,
  refCode: "",
  receiveBusOrderRefCode: "",
  textNote: "",
  assigneeSelections: [],
  images: [],
});

const selectProps = {
  className: "basic-single w-100",
  classNamePrefix: "select2-selection",
};

/** Dropdown renders in portal so options are not clipped inside Modal body */
const asyncModalMenuProps =
  typeof document !== "undefined"
    ? {
      menuPortalTarget: document.body,
      styles: {
        menuPortal: base => ({ ...base, zIndex: 2050 }),
      },
    }
    : {};

/**
 * Modal tạo ticket — POST /ticket (JSON): hub_id, creator_id, type_id, order_ref_code, receive_bus_order_ref_code, resource_ids, assignees.
 * Ảnh: POST `ticket-resource/upload-file` (form-data `file`), png/jpg/jpeg tối đa 5MB. Mã tham chiếu nhập/quét trực tiếp, không gọi tra cứu đơn.
 * Props: isOpen, toggle, onSubmit(async payload), isSubmitting, hubId, creatorId, isResolvingCreator
 */
const TicketCreateModal = props => {
  const {
    isOpen,
    toggle,
    onSubmit,
    isSubmitting,
    hubId,
    creatorId,
    isResolvingCreator = false,
  } = props;
  const { t } = useTranslation("ticketManagement");
  const { addToast } = useToasts();

  const [formValue, setFormValue] = useState(getInitialFormValue);
  const [isBusy, setIsBusy] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const { data: ticketTypesRes, isLoading: isLoadingTicketTypes } = useQuery(
    ["ticket_types_ajax"],
    () => ticketService.listTicketTypes(),
    {
      enabled: isOpen,
      staleTime: 60_000,
      retry: 1,
    }
  );

  const ticketTypeOptions = useMemo(() => {
    const body = ticketTypesRes?.data;
    return mapTicketTypeOptions(body);
  }, [ticketTypesRes]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setFormValue(getInitialFormValue());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || ticketTypeOptions.length !== 1) {
      return;
    }
    const onlyTicketType = ticketTypeOptions[0];
    setFormValue(prev => {
      const currentTicketTypeId = Number(prev.ticketType?.value);
      if (currentTicketTypeId === Number(onlyTicketType.value)) {
        return prev;
      }
      return {
        ...prev,
        ticketType: onlyTicketType,
      };
    });
    setFormErrors(prev => ({ ...prev, ticketType: "" }));
  }, [isOpen, ticketTypeOptions]);

  const isFormLocked = isSubmitting || isBusy;

  const handleChangeField = useCallback((field, value) => {
    setFormValue(prev => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors(prev => ({ ...prev, [field]: "" }));
  }, []);

  const handleAddImages = files => {
    setFormErrors(prev => ({ ...prev, images: "" }));
    handleChangeField("images", [...(formValue.images || []), ...files]);
  };

  const handleRemoveImage = removedIndex => {
    handleChangeField(
      "images",
      (formValue.images || []).filter((_, index) => index !== removedIndex)
    );
  };

  const uploadImagesToResourceIds = async files => {
    const ids = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const data = new FormData();
      data.append("file", file);
      const res = await ticketService.uploadTicketResource(data);
      const num = extractTicketUploadResourceId(res);
      if (!Number.isFinite(num)) {
        throw new Error("upload_resource_id");
      }
      ids.push(num);
    }
    return ids;
  };

  const handleSave = async () => {
    const nextErrors = {};
    const fallbackTicketTypeId =
      ticketTypeOptions.length === 1 ? Number(ticketTypeOptions[0].value) : NaN;
    const selectedTypeId = Number(formValue.ticketType?.value || fallbackTicketTypeId);

    if (!Number.isFinite(selectedTypeId) || selectedTypeId <= 0) {
      nextErrors.ticketType = t("toast.createSelectType");
    }

    const order_ref_code = String(formValue.refCode || "").trim();
    if (!order_ref_code) {
      nextErrors.refCode = t("toast.createMissingRefCode");
    }

    const receive_bus_order_ref_code = normalizeReceiveBusOrderRefCode(
      formValue.receiveBusOrderRefCode
    );

    const assigneeErrors = validateTicketAssigneeSelections({
      assigneeSelections: formValue.assigneeSelections,
      requiredMessage: t("saleAssignment.requiredAssignees"),
    });
    Object.assign(nextErrors, assigneeErrors);

    if (!formValue.images?.length) {
      nextErrors.images = t("toast.createSelectImage");
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const effectiveHubIdCandidate =
      hubId != null && Number.isFinite(Number(hubId)) ? Number(hubId) : null;

    if (effectiveHubIdCandidate == null) {
      addToast(t("toast.createMissingHub"), {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    if (isResolvingCreator) {
      addToast(t("toast.createCreatorLoading"), {
        appearance: "info",
        autoDismiss: true,
      });
      return;
    }

    if (creatorId == null || !Number.isFinite(Number(creatorId))) {
      addToast(t("toast.createCreatorUnknown"), {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    const { assignees } = buildTicketAssigneesPayload({
      assigneeSelections: formValue.assigneeSelections,
    });

    setIsBusy(true);
    const files = formValue.images || [];
    let resourceIds = [];
    try {
      if (files.length > 0) {
        resourceIds = await uploadImagesToResourceIds(files);
      }
    } catch {
      addToast(t("toast.createUploadFailed"), {
        appearance: "error",
        autoDismiss: true,
      });
      setIsBusy(false);
      return;
    }

    const payload = {
      hub_id: effectiveHubIdCandidate,
      creator_id: Number(creatorId),
      type_id: selectedTypeId,
      order_ref_code,
      ...(receive_bus_order_ref_code ? { receive_bus_order_ref_code } : {}),
      note: String(formValue.textNote || "").trim() || undefined,
      resource_ids: resourceIds,
      assignees,
    };

    try {
      await onSubmit(payload);
    } finally {
      setIsBusy(false);
    }
  };

  const disablePrimary = isFormLocked || isLoadingTicketTypes;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="md"
      trapFocus={false}
      wrapClassName="ticket-create-modal-dialog"
      contentClassName="rounded ticket-modal-compact"
    >
      <ModalHeader toggle={toggle}>Tạo ticket</ModalHeader>
      <ModalBody className="pt-2 pb-1">
        <FormGroup className="mb-2">
          <Label>
            Tên ticket <span className="text-danger">*</span>
          </Label>
          <Select
            {...selectProps}
            {...asyncModalMenuProps}
            options={ticketTypeOptions}
            value={formValue.ticketType}
            onChange={option => handleChangeField("ticketType", option)}
            placeholder={
              isLoadingTicketTypes
                ? "Đang tải danh sách..."
                : "Chọn tên ticket"
            }
            isSearchable={false}
            isLoading={isLoadingTicketTypes}
            isDisabled={isFormLocked || ticketTypeOptions.length === 0}
          />
          {formErrors.ticketType ? (
            <small className="text-danger d-block mt-1">{formErrors.ticketType}</small>
          ) : null}
        </FormGroup>

        <FormGroup className="mb-2">
          <TicketBarcodeInput
            id="ticket-create-ref-code"
            label="Mã tham chiếu"
            isRequired
            value={formValue.refCode}
            placeholder="Nhập hoặc quét mã tham chiếu"
            onChange={value => handleChangeField("refCode", value)}
            disabled={isFormLocked}
            invalid={!!formErrors.refCode}
            errorMessage={formErrors.refCode}
          />
        </FormGroup>

        <TicketSaleAssignmentFields
          isOpen={isOpen}
          isDisabled={isFormLocked}
          formValue={formValue}
          formErrors={formErrors}
          onFieldChange={handleChangeField}
        />

        <FormGroup className="mb-2">
          <Label>
            Ảnh ticket (tối đa 2 ảnh) <span className="text-danger">*</span>
          </Label>
          <TicketImageField
            isDisabled={isFormLocked}
            errorMessage={formErrors.images}
            maxCount={2}
            selectedCount={formValue.images?.length || 0}
            newFiles={formValue.images || []}
            onAddFiles={handleAddImages}
            onRemoveNewFile={handleRemoveImage}
          />
        </FormGroup>

        <FormGroup className="mb-2">
          <TicketBarcodeInput
            id="ticket-create-receive-bus-ref"
            label="Mã đơn lấy hàng"
            value={formValue.receiveBusOrderRefCode}
            placeholder="Nhập hoặc quét mã đơn lấy hàng"
            onChange={value => handleChangeField("receiveBusOrderRefCode", value)}
            disabled={isFormLocked}
          />
        </FormGroup>

        <FormGroup className="mb-2">
          <Label>Mô tả</Label>
          <Input
            type="textarea"
            rows={1}
            className="form-control"
            value={formValue.textNote}
            placeholder="Nhập mô tả"
            onChange={event =>
              handleChangeField("textNote", event.target.value)
            }
            disabled={isFormLocked}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={isFormLocked}>
          Hủy
        </Button>
        <Button color="primary" onClick={handleSave} disabled={disablePrimary}>
          {isFormLocked ? "Đang xử lý..." : "Tạo ticket"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TicketCreateModal;
